import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleTable, Button, Input } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { getProductColumns } from './tableColumns';
import { getProducts } from '../../../api/ProductApi';
import styles from './Products.module.scss';
const Products = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const latestProductsRequestId = useRef(0);
    useEffect(() => () => {
        latestProductsRequestId.current += 1;
    }, []);
    useEffect(() => {
        if (!searchTerm.trim()) {
            fetchProducts('');
            return;
        }
        const timeoutId = window.setTimeout(() => {
            fetchProducts(searchTerm);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);
    const fetchProducts = async (search = '') => {
        const requestId = ++latestProductsRequestId.current;
        try {
            setLoading(true);
            const trimmedSearch = search.trim();
            const response = await getProducts({ search: trimmedSearch || undefined });
            const data = response.body;
            const productsList = Array.isArray(data) ? data : data?.results || [];
            if (requestId !== latestProductsRequestId.current) {
                return;
            }
            setProducts(Array.isArray(productsList) ? productsList : [productsList]);
        }
        catch (error) {
            if (requestId !== latestProductsRequestId.current) {
                return;
            }
            console.error('Error fetching products:', error);
            setProducts([]);
        }
        finally {
            if (requestId === latestProductsRequestId.current) {
                setLoading(false);
            }
        }
    };
    const handleView = (product) => {
        navigate(`/web-admin/products/${product.id}`);
    };
    const handleEdit = (product) => {
        navigate(`/web-admin/products/${product.id}`);
    };
    const getCsrfToken = () => {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split('=');
            if (name === 'csrftoken') {
                return value;
            }
        }
        return null;
    };
    const handleDelete = async (product) => {
        if (window.confirm(`Are you sure you want to delete "${product.title}"?`)) {
            try {
                const response = await fetch(`/api/v1/products/${product.id}/`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                        'X-CSRFToken': getCsrfToken() || '',
                    },
                });
                if (!response.ok) {
                    throw new Error('Failed to delete product');
                }
                await fetchProducts(searchTerm);
            }
            catch (error) {
                console.error('Error deleting product:', error);
                alert('Failed to delete product');
            }
        }
    };
    const handleAddProduct = () => {
        navigate('/web-admin/products/new');
    };
    const columns = getProductColumns(handleView, handleEdit, handleDelete);
    return (_jsx(AdminLayout, { children: _jsxs("div", { className: styles.container, children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by product, SKU, category, or manufacturer", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) }), _jsx(SimpleTable, { columns: columns, data: products, rowHeight: "60px", tableHeading: "Products", titleStyle: {
                                color: '#000',
                                fontFamily: 'Nunito',
                                fontSize: '24px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '28px'
                            }, hideTopBorder: true, enablePagination: true, paginationText: "Products per page", pageSize: 20, filterColumn: "manufacturer", filterLabel: "Show All Manufacturers", onFilterChange: (value) => {
                                // Filter logic will be handled by SimpleTable
                                console.log('Filter by manufacturer:', value);
                            }, onRowClick: (product) => handleView(product), customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Product", onClick: handleAddProduct }) })] })] }) }));
};
export default Products;
